/**
 * Reward Store / Family Coins (§22.9) — parent control.
 *
 * Per child: coin balance + quick "award" buttons (chores, lessons, …) and a
 * recent ledger. Family-wide: pending redemptions to fulfil/deny, the reward
 * catalogue (add / edit cost / remove), and tunable earning-rule amounts.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Coins, Plus, Trash2, Check, X, Gift } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usersApi } from '@/api/users';
import { rewardsApi, type EarnReason } from '@/api/rewards';
import { ChildPicker } from './ChildPicker';
import { PageHeader } from './PageHeader';

const AWARD_BUTTONS: { reason: EarnReason; label: string }[] = [
  { reason: 'chore', label: 'Chore' },
  { reason: 'safety_lesson', label: 'Safety lesson' },
  { reason: 'daily_routine', label: 'Daily routine' },
];

const REASON_LABEL: Record<string, string> = {
  daily_routine: 'Daily routine',
  screen_time_respected: 'Respected screen time',
  focus_completed: 'Finished focus mode',
  bedtime_followed: 'Followed bedtime',
  arrived_home: 'Arrived home on time',
  chore: 'Chore',
  safety_lesson: 'Safety lesson',
  redemption: 'Reward redeemed',
  redemption_refund: 'Refund',
  manual: 'Manual',
};

export function RewardsPage() {
  const { t } = useTranslation();
  const children = useQuery({ queryKey: ['children'], queryFn: () => usersApi.listChildren() });
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);

  return (
    <div className="px-6 py-8 md:px-10 md:py-10">
      <PageHeader
        title={t('pages.rewards.title')}
        icon={Coins}
        tint="amber"
        description={t('pages.rewards.description')}
      />

      <PendingRedemptions childrenById={new Map((children.data ?? []).map((c) => [c.id, c.name]))} />

      <div className="mt-6">
        <ChildPicker
          children={children.data ?? []}
          selectedId={selectedChildId}
          onSelect={setSelectedChildId}
        />
      </div>

      {selectedChildId && (
        <div className="mt-6">
          <BalanceCard childId={selectedChildId} />
        </div>
      )}

      <Tabs defaultValue="catalogue" className="mt-6">
        <TabsList>
          <TabsTrigger value="catalogue">Reward store</TabsTrigger>
          <TabsTrigger value="rules">Earning rules</TabsTrigger>
        </TabsList>
        <TabsContent value="catalogue">
          <CataloguePanel />
        </TabsContent>
        <TabsContent value="rules">
          <RulesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BalanceCard({ childId }: { childId: string }) {
  const queryClient = useQueryClient();
  const account = useQuery({
    queryKey: ['rewards', 'account', childId],
    queryFn: () => rewardsApi.account(childId),
  });
  const ledger = useQuery({
    queryKey: ['rewards', 'ledger', childId],
    queryFn: () => rewardsApi.ledger(childId, 12),
  });

  const award = useMutation({
    mutationFn: (reason: EarnReason) => rewardsApi.award(childId, { reason }),
    onSuccess: (acct) => {
      toast.success(`Coins awarded — balance ${acct.balance}.`);
      void queryClient.invalidateQueries({ queryKey: ['rewards', 'account', childId] });
      void queryClient.invalidateQueries({ queryKey: ['rewards', 'ledger', childId] });
    },
    onError: () => toast.error('Could not award coins.'),
  });

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-4">
          <span className="grid size-14 place-items-center rounded-full bg-amber-100">
            <Coins className="size-7 text-amber-600" />
          </span>
          <div className="flex-1">
            <p className="text-3xl font-semibold tabular-nums">{account.data?.balance ?? '—'}</p>
            <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
              coins · {account.data?.lifetime_earned ?? 0} earned all-time
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {AWARD_BUTTONS.map((b) => (
              <Button key={b.reason} size="sm" variant="outline" onClick={() => award.mutate(b.reason)} disabled={award.isPending}>
                <Plus className="mr-1 size-3.5" />
                {b.label}
              </Button>
            ))}
          </div>
        </div>

        {(ledger.data ?? []).length > 0 && (
          <ul className="mt-4 divide-y divide-[color:var(--kz-color-border-subtle)] border-t border-[color:var(--kz-color-border-subtle)]">
            {(ledger.data ?? []).map((t) => (
              <li key={t.id} className="flex items-center gap-3 py-2 text-sm">
                <span className={t.amount >= 0 ? 'font-semibold text-emerald-600 tabular-nums' : 'font-semibold text-rose-600 tabular-nums'}>
                  {t.amount >= 0 ? '+' : ''}{t.amount}
                </span>
                <span className="flex-1 truncate text-[color:var(--kz-color-ink-secondary)]">
                  {t.note ?? REASON_LABEL[t.reason] ?? t.reason}
                </span>
                <span className="shrink-0 text-xs text-[color:var(--kz-color-ink-muted)]">
                  {new Date(t.created_at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function PendingRedemptions({ childrenById }: { childrenById: Map<string, string> }) {
  const queryClient = useQueryClient();
  const redemptions = useQuery({
    queryKey: ['rewards', 'redemptions'],
    queryFn: () => rewardsApi.redemptions(true),
    refetchInterval: 30_000,
  });
  const decide = useMutation({
    mutationFn: ({ id, fulfill }: { id: string; fulfill: boolean }) =>
      rewardsApi.decideRedemption(id, fulfill),
    onSuccess: (_d, v) => {
      toast.success(v.fulfill ? 'Reward fulfilled.' : 'Redemption denied — coins refunded.');
      void queryClient.invalidateQueries({ queryKey: ['rewards', 'redemptions'] });
      void queryClient.invalidateQueries({ queryKey: ['rewards', 'account'] });
    },
  });

  const items = redemptions.data ?? [];
  if (items.length === 0) return null;

  return (
    <Card className="mt-6 border-amber-200 bg-amber-50/40">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Gift className="size-4 text-amber-600" />
          Reward requests
        </CardTitle>
        <CardDescription>A child wants to spend their coins — approve to fulfil, or deny to refund.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {items.map((r) => (
            <li key={r.id} className="flex items-center gap-3 rounded-card bg-white/70 p-3 text-sm">
              <div className="flex-1 min-w-0">
                <p className="font-medium">{r.reward_name}</p>
                <p className="text-xs text-[color:var(--kz-color-ink-muted)]">
                  {childrenById.get(r.child_id) ?? 'Child'} · {r.cost_coins} coins
                </p>
              </div>
              <Button size="sm" onClick={() => decide.mutate({ id: r.id, fulfill: true })} disabled={decide.isPending}>
                <Check className="mr-1 size-3.5" /> Fulfil
              </Button>
              <Button size="sm" variant="ghost" onClick={() => decide.mutate({ id: r.id, fulfill: false })} disabled={decide.isPending}>
                <X className="mr-1 size-3.5" /> Deny
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function CataloguePanel() {
  const queryClient = useQueryClient();
  const catalogue = useQuery({ queryKey: ['rewards', 'catalogue'], queryFn: () => rewardsApi.catalogue() });
  const [name, setName] = useState('');
  const [cost, setCost] = useState('');
  const [icon, setIcon] = useState('');

  const create = useMutation({
    mutationFn: () =>
      rewardsApi.createReward({ name: name.trim(), cost_coins: parseInt(cost, 10) || 1, icon: icon.trim() || undefined }),
    onSuccess: () => {
      setName(''); setCost(''); setIcon('');
      void queryClient.invalidateQueries({ queryKey: ['rewards', 'catalogue'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Add failed.'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => rewardsApi.deleteReward(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rewards', 'catalogue'] }),
  });

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Add a reward</CardTitle>
          <CardDescription>Set what coins can buy. Ships with starter templates.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={(e) => { e.preventDefault(); if (name.trim() && cost) create.mutate(); }}
            className="grid gap-3 md:grid-cols-[2fr_1fr_1fr_auto] md:items-end"
          >
            <div className="grid gap-1.5">
              <Label htmlFor="r-name">Reward</Label>
              <Input id="r-name" placeholder="Movie night" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="r-cost">Cost (coins)</Label>
              <Input id="r-cost" type="number" min={1} placeholder="300" value={cost} onChange={(e) => setCost(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="r-icon">Emoji</Label>
              <Input id="r-icon" placeholder="🎬" value={icon} onChange={(e) => setIcon(e.target.value)} />
            </div>
            <Button type="submit" size="sm" disabled={!name.trim() || !cost || create.isPending}>
              <Plus className="size-4" /> Add
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Reward store</CardTitle></CardHeader>
        <CardContent>
          <ul className="divide-y divide-[color:var(--kz-color-border-subtle)]">
            {(catalogue.data ?? []).map((r) => (
              <li key={r.id} className="flex items-center gap-3 py-2.5">
                <span className="text-xl">{r.icon ?? '🎁'}</span>
                <span className="flex-1 truncate text-sm font-medium">{r.name}</span>
                <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  {r.cost_coins} coins
                </span>
                <Button variant="ghost" size="icon" onClick={() => remove.mutate(r.id)} aria-label="Remove">
                  <Trash2 className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function RulesPanel() {
  const queryClient = useQueryClient();
  const rules = useQuery({ queryKey: ['rewards', 'rules'], queryFn: () => rewardsApi.earningRules() });
  const save = useMutation({
    mutationFn: (input: { reason: EarnReason; coins: number; enabled: boolean }) =>
      rewardsApi.setEarningRule(input),
    onSuccess: () => {
      toast.success('Rule saved.');
      void queryClient.invalidateQueries({ queryKey: ['rewards', 'rules'] });
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Earning rules</CardTitle>
        <CardDescription>How many coins each healthy habit earns. Tune to fit your family.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-[color:var(--kz-color-border-subtle)]">
          {(rules.data ?? []).map((rule) => (
            <RuleRow key={rule.reason} rule={rule} onSave={(coins) => save.mutate({ reason: rule.reason, coins, enabled: true })} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

function RuleRow({
  rule,
  onSave,
}: {
  rule: { reason: EarnReason; coins: number; enabled: boolean };
  onSave: (coins: number) => void;
}) {
  const [coins, setCoins] = useState(String(rule.coins));
  const dirty = coins !== String(rule.coins);
  return (
    <li className="flex items-center gap-3 py-2.5">
      <span className="flex-1 text-sm">{REASON_LABEL[rule.reason] ?? rule.reason}</span>
      <Input
        type="number"
        min={0}
        className="h-8 w-24"
        value={coins}
        onChange={(e) => setCoins(e.target.value)}
      />
      <Button size="sm" variant={dirty ? 'default' : 'outline'} disabled={!dirty} onClick={() => onSave(parseInt(coins, 10) || 0)}>
        Save
      </Button>
    </li>
  );
}
